import React from 'react';

// Rows-per-page dropdown. A native <select> opens wherever the browser decides
// (usually downward), which at the bottom-pinned pagination bar drops the option
// list off the popup/viewport edge — a custom trigger with an upward-anchored
// menu (CSS bottom: 100%, .ps-* in bravo-app.css) always opens above the bar.
const PageSizeSelect = (props) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      className='ps-select'
      ref={rootRef}
    >
      <button
        type='button'
        className='ps-btn'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {props.value}
        <i className='fa-solid fa-chevron-up' />
      </button>
      {open && (
        <ul
          className='ps-menu'
          role='listbox'
        >
          {props.options.map((x) => (
            <li
              key={x}
              role='option'
              aria-selected={Number(props.value) === x}
            >
              <button
                type='button'
                className={`ps-opt${Number(props.value) === x ? ' sel' : ''}`}
                onClick={() => {
                  setOpen(false);
                  props.onChange(String(x));
                }}
              >
                {x}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

const Pagination = (props) => {
  const getRangeOpts = () => {
    const fullList = [20, 50, 100, 200, 500, 1000, 5000];
    if (props.rangeMax >= 5000) return fullList;
    return fullList.filter((x) => x <= props.rangeMax);
  };

  const renderRangeSelector = () => {
    if (props.onChangeOfRangeSelector === null) return null;
    return (
      <div className='page-size'>
        Rows per page
        <PageSizeSelect
          value={props.numberOfElementsOnThePage}
          options={getRangeOpts()}
          onChange={(value) => props.onChangeOfRangeSelector(value)}
        />
      </div>
    );
  };

  const renderPageButton = (text, i, actualPage, key) => (
    <button
      type='button'
      key={key}
      className={i === actualPage ? 'cur' : undefined}
      onClick={i === actualPage ? undefined : () => props.onClick(i)}
    >
      {text}
    </button>
  );

  const renderPager = (actualPage, lastPage) => {
    const items = [];

    items.push(
      <button
        type='button'
        key='first'
        disabled={actualPage <= 1}
        onClick={() => props.onClick(1)}
      >
        <i className='fa-solid fa-angles-left' />
      </button>
    );
    items.push(
      <button
        type='button'
        key='prev'
        disabled={actualPage <= 1}
        onClick={() => props.onClick(actualPage - 1)}
      >
        <i className='fa-solid fa-angle-left' />
      </button>
    );

    const start = actualPage > 2 ? actualPage - 2 : 1;
    const end = actualPage < lastPage - 2 ? actualPage + 2 : lastPage;

    if (actualPage > 3) {
      items.push(renderPageButton(1, 1, actualPage, 'page-first'));
      items.push(
        <span
          className='pager-gap'
          key='gap-start'
        >
          …
        </span>
      );
    }

    for (let i = start; i <= end; i++) {
      items.push(renderPageButton(i, i, actualPage, `page-${i}`));
    }

    if (actualPage < lastPage - 2) {
      items.push(
        <span
          className='pager-gap'
          key='gap-end'
        >
          …
        </span>
      );
      items.push(renderPageButton(lastPage, lastPage, actualPage, 'page-last'));
    }

    items.push(
      <button
        type='button'
        key='next'
        disabled={actualPage >= lastPage}
        onClick={() => props.onClick(actualPage + 1)}
      >
        <i className='fa-solid fa-angle-right' />
      </button>
    );
    items.push(
      <button
        type='button'
        key='last'
        disabled={actualPage >= lastPage}
        onClick={() => props.onClick(lastPage)}
      >
        <i className='fa-solid fa-angles-right' />
      </button>
    );

    return items;
  };

  if (
    props.numberOfElementsInTheTable === 0 ||
    props.numberOfElementsInTheTable === undefined ||
    props.numberOfElementsInTheTable === null
  ) {
    return null;
  }

  const total = props.numberOfElementsInTheTable;
  const pageSize = parseFloat(props.numberOfElementsOnThePage);
  const lastPage = Math.ceil(total / pageSize);
  const start = (props.pageNumber - 1) * pageSize + 1;
  const end = Math.min(props.pageNumber * pageSize, total);

  return (
    <div className='bravo-ds'>
      <div className='foot'>
        <span>
          Showing{' '}
          <b>
            {start}–{end}
          </b>{' '}
          of {total} {props.itemTypeName}
        </span>
        <div className='foot-right'>
          {renderRangeSelector()}
          {lastPage > 1 && <div className='pager'>{renderPager(props.pageNumber, lastPage)}</div>}
        </div>
      </div>
    </div>
  );
};

Pagination.defaultProps = {
  pageNumber: 1,
  numberOfElementsInTheTable: 1,
  numberOfElementsOnThePage: 20,
  onClick: () => {},
  onChangeOfRangeSelector: null, // if not null, the rows-per-page selector is shown (must be a function)
  rangeMax: 5000,
  itemTypeName: 'items',
};

export default Pagination;
